import { makeStyles } from '@material-ui/styles'
import { keys } from 'ramda'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { listNetworkServiceDetails } from 'app/plugins/infrastructure/components/common/service-health/action'
import { useControlPlaneServicesCheck } from 'app/plugins/infrastructure/components/common/service-health/ControlPlaneServicesContext'
import { networkServicesSelector } from 'app/plugins/infrastructure/components/common/service-health/selector'
import { useVMHAPolling } from 'app/plugins/openstack/components/infrastructure/hosts/vmha'
import { IHealthServiceDetailsPageTabs } from 'app/plugins/openstack/components/networks/model'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import BannerContainer from 'core/components/notifications/BannerContainer'
import BannerContent from 'core/components/notifications/BannerContent'
import PollingData from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import type Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { listResmgrHosts } from 'openstack/components/infrastructure/actions'
import {
  ControlPlaneServiceUnhealthyMessage,
  getHeaderAlertMessage,
  hasCinderRole,
  hasHypervisorRole,
  hasImageLibraryRole,
  hostUnhealthy,
} from 'openstack/components/infrastructure/helpers'
import { resmgrHostsSelector } from 'openstack/components/infrastructure/selectors'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  headerAlert: {
    display: 'grid',
    alignItems: 'center',
    alignSelf: 'center',
    gridTemplateColumns: 'max-content max-content',
    gap: 8,
  },
  headerIcon: {
    color: theme.palette.red[500],
  },
  headerAlertCancel: {
    alignSelf: 'center',
    cursor: 'pointer',
    padding: theme.spacing(0, 1),
  },
}))

export const getUnderlayInfo = (blueprintSettings, hostConfigs) => {
  const underlayType = blueprintSettings?.virtualNetworking?.underlayType
  const vnidRange = blueprintSettings?.virtualNetworking?.vnidRange || ''
  const vniRanges = vnidRange
    .split(',')
    .map((range) => range.trim())
    .map((range) => {
      const parts = range.split(':')
      return parts[0] === parts[1] ? parts[1] : `${parts[0]}-${parts[1]}`
    })
  const firstHostConfig = hostConfigs?.[0]
  const tunnelPhysnet = firstHostConfig?.tunnelingInterface
  const labelKeys = keys(firstHostConfig?.networkLabels)
  const tunnelLabel = labelKeys?.find((key) => {
    return firstHostConfig?.networkLabels?.[key] === tunnelPhysnet
  })
  return {
    type: underlayType,
    label: tunnelLabel,
    ranges: vniRanges,
  }
}

const AppBanners = () => {
  const classes = useStyles()
  const [headerAlertMessage, setHeaderAlertMessage] = useState('')
  const [awaitingHeaderCheck, setAwaitingHeaderCheck] = useState(true)
  const [vmhaAlertDismissed, setVmhaAlertDismissed] = useState(false)

  // Whether to show the header alert - initialized to true and set to false when the alert is dismissed by the user
  const showHeaderAlert = useRef(true)
  const showVMHAAlert = useRef(true)

  // Load hosts list for health checks
  const { loading: loadingResmgrHosts, reload: reloadResmgrHosts } = useListAction(
    listResmgrHosts,
    {
      initialLoadingState: true,
    },
  )
  const resmgrHosts = useSelector(resmgrHostsSelector)

  // Load Networks for health checks
  const { reload: reloadNetworkService } = useListAction(listNetworkServiceDetails)
  const networkServiceDetails = useSelector(networkServicesSelector)

  const {
    serviceHealthStates: controlPlaneServiceHealthStates,
    lastUpdated: controlPlaneServiceHealthStatesLastUpdated,
  } = useControlPlaneServicesCheck()

  useEffect(() => {
    // Do not show the header alert again if the user dismissed it
    if (!showHeaderAlert.current) return
    const hasMajorityUnhealthy = <T,>(items: T[], isUnhealthy: (item: T) => boolean): boolean => {
      if (items.length === 0) return false
      return items.filter(isUnhealthy)?.length > items.length / 2
    }

    /**
     * Check infrastructure health (runs on init and on resmgr host polling) (takes precedence over control plane)
     */
    if (!loadingResmgrHosts && awaitingHeaderCheck) {
      const hypervisors = resmgrHosts.filter(hasHypervisorRole)
      const imageLibraries = resmgrHosts.filter(hasImageLibraryRole)
      const blockStorageNodes = resmgrHosts.filter(hasCinderRole)
      const networkNodes = networkServiceDetails ?? []
      const alertMessage = getHeaderAlertMessage({
        hypervisor: hasMajorityUnhealthy(hypervisors, hostUnhealthy),
        imageLibrary: hasMajorityUnhealthy(imageLibraries, hostUnhealthy),
        blockStorage: hasMajorityUnhealthy(blockStorageNodes, hostUnhealthy),
        network: hasMajorityUnhealthy(networkNodes, (service) => service?.status !== 'Healthy'),
      })
      setAwaitingHeaderCheck(false)
      setHeaderAlertMessage(alertMessage)
      return
    }

    /**
     * Check control plane health
     */
    if ((controlPlaneServiceHealthStates?.unhealthy ?? 0) > 0) {
      setAwaitingHeaderCheck(false)
      setHeaderAlertMessage(ControlPlaneServiceUnhealthyMessage)
      return
    }

    // Reset header alert message if everything is healthy
    setHeaderAlertMessage('')
  }, [
    loadingResmgrHosts,
    resmgrHosts,
    awaitingHeaderCheck,
    networkServiceDetails,
    controlPlaneServiceHealthStates,
    controlPlaneServiceHealthStatesLastUpdated,
  ])

  // VMHA polling hook
  const { ongoingVHMAEvent } = useVMHAPolling()

  // Reset dismissal state when a new VMHA event appears
  useEffect(() => {
    if (ongoingVHMAEvent && !showVMHAAlert.current) {
      showVMHAAlert.current = true
      setVmhaAlertDismissed(false)
    }
  }, [ongoingVHMAEvent?.event?.vmhaUuid])

  const showVMHABanner = useMemo(
    () => isAdmin() && showVMHAAlert.current && ongoingVHMAEvent && !vmhaAlertDismissed,
    [isAdmin, ongoingVHMAEvent, vmhaAlertDismissed],
  )

  return (
    <>
      <BannerContainer />
      {showVMHABanner ? (
        <BannerContent>
          <div />
          <div className={classes.headerAlert}>
            <FontAwesomeIcon className={classes.headerIcon}>exclamation-triangle</FontAwesomeIcon>
            <Text variant="caption1">
              High Availability service detected host {ongoingVHMAEvent.event?.instanceName} from
              cluster {ongoingVHMAEvent.event?.cluster} has failed.{' '}
              <SimpleLink
                src={routeManager.routes.openstack.hostDetails.path({
                  id: ongoingVHMAEvent.event?.hostId,
                  tab: 'overview',
                  showVMHADetails: 'true',
                })}
              >
                <strong>View details</strong>
              </SimpleLink>
            </Text>
          </div>
          <FontAwesomeIcon
            className={classes.headerAlertCancel}
            onClick={() => {
              // Changing the value of ref do not trigger re-render and banner will not be hidden
              // So we need to set vmhaAlertDismissed to true to hide the banner
              showVMHAAlert.current = false
              setVmhaAlertDismissed(true)
            }}
          >
            xmark
          </FontAwesomeIcon>
        </BannerContent>
      ) : showHeaderAlert.current && headerAlertMessage ? (
        <>
          {/* Poll every 10 minutes when banner is showing */}
          <PollingData
            hidden
            loading={loadingResmgrHosts}
            onReload={() => {
              setAwaitingHeaderCheck(true)
              reloadNetworkService(true, false)
              return reloadResmgrHosts(true, false)
            }}
            refreshDuration={600000}
          />
          <BannerContent>
            <div />
            <div className={classes.headerAlert}>
              <FontAwesomeIcon className={classes.headerIcon}>exclamation-triangle</FontAwesomeIcon>
              <Text variant="caption1">
                {headerAlertMessage}{' '}
                {isAdmin() && (
                  <SimpleLink
                    src={routeManager.routes.openstack.health.details.path({
                      tab: headerAlertMessage.includes(ControlPlaneServiceUnhealthyMessage)
                        ? IHealthServiceDetailsPageTabs.ControlPlaneServicesHealth
                        : IHealthServiceDetailsPageTabs.ServiceHealth,
                    })}
                    textVariant="caption1"
                  >
                    See Details
                  </SimpleLink>
                )}
              </Text>
            </div>
            <FontAwesomeIcon
              className={classes.headerAlertCancel}
              onClick={() => {
                showHeaderAlert.current = false
                setHeaderAlertMessage('')
              }}
            >
              xmark
            </FontAwesomeIcon>
          </BannerContent>
        </>
      ) : null}
    </>
  )
}

export default AppBanners
